import React from 'react';
import { Box, Tooltip, IconButton, Chip, Typography } from '@mui/material';
import { ContentCut } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  activateSplitTool,
  deactivateSplitTool,
  selectIsSplitToolActive,
  selectSplitFeatureId,
} from '../store/slices/geometryEditingSlice';
import { selectSelectedFeatures } from '../store/slices/selectionSlice';

/**
 * SplitToolToggle
 *
 * A toolbar button that toggles the polygon split tool for the currently
 * selected polygon feature. When activated, the user can draw a split line
 * across the polygon to divide it into two separate features.
 *
 * **Validates: Requirement 14.1** – WHEN a Registered_User selects a polygon
 * Feature and activates split tool, THE Vector_Editor SHALL enable drawing
 * a split line.
 *
 * Behaviour:
 * - Disabled when no feature is selected or when multiple features are selected
 * - Clicking toggles the split tool on/off for the selected feature
 * - Shows a status chip when the split tool is active
 * - Mutually exclusive with node editing mode
 */
const SplitToolToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const isSplitToolActive = useAppSelector(selectIsSplitToolActive);
  const splitFeatureId = useAppSelector(selectSplitFeatureId);
  const selectedFeatures = useAppSelector(selectSelectedFeatures);

  const hasSingleSelection = selectedFeatures.length === 1;
  const selectedFeature = hasSingleSelection ? selectedFeatures[0] : null;

  const canActivate = hasSingleSelection && !isSplitToolActive;
  const canDeactivate = isSplitToolActive;
  const isDisabled = !canActivate && !canDeactivate;

  const handleToggle = () => {
    if (isSplitToolActive) {
      dispatch(deactivateSplitTool());
    } else if (selectedFeature) {
      dispatch(
        activateSplitTool({
          featureId: selectedFeature.featureId,
          layerId: selectedFeature.layerId,
        })
      );
    }
  };

  const getTooltipText = (): string => {
    if (isSplitToolActive) {
      return 'Exit Split Tool';
    }
    if (!hasSingleSelection) {
      return 'Select a single polygon to split';
    }
    return 'Split Polygon';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={getTooltipText()} placement="bottom" arrow>
        <span>
          <IconButton
            size="small"
            onClick={handleToggle}
            disabled={isDisabled}
            aria-label={isSplitToolActive ? 'Exit split tool' : 'Activate split tool'}
            aria-pressed={isSplitToolActive}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              color: isSplitToolActive ? '#4fc3f7' : '#c8c8c8',
              backgroundColor: isSplitToolActive ? 'rgba(79,195,247,0.15)' : 'transparent',
              border: isSplitToolActive
                ? '1px solid rgba(79,195,247,0.4)'
                : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
              },
              '&.Mui-disabled': {
                color: '#555',
              },
            }}
          >
            <ContentCut sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Status indicator when split tool is active */}
      {isSplitToolActive && splitFeatureId && (
        <Chip
          label={`Split mode: ${splitFeatureId}`}
          size="small"
          onDelete={handleToggle}
          sx={{
            backgroundColor: 'rgba(79,195,247,0.12)',
            color: '#4fc3f7',
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 200,
            border: '1px solid rgba(79,195,247,0.3)',
            '& .MuiChip-deleteIcon': {
              color: '#4fc3f7',
              fontSize: 14,
              '&:hover': {
                color: '#fff',
              },
            },
          }}
        />
      )}

      {/* Hint when no feature is selected */}
      {!isSplitToolActive && selectedFeatures.length === 0 && (
        <Typography sx={{ color: '#555', fontSize: '0.65rem' }}>
          Select a polygon to split
        </Typography>
      )}
    </Box>
  );
};

export default SplitToolToggle;
